import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ShieldCheck, Lock, Tag, X, Heart } from "lucide-react";
import api from "../lib/api";
import { formatApiError } from "../lib/errors";
import { useCart } from "../context/CartContext";
import { getQrAttribution } from "../lib/qrAttribution";
import { toast } from "sonner";
import PayPalCheckoutButtons from "../components/PayPalCheckoutButtons";
import StripePaymentForm from "../components/StripePaymentForm";
import { OfflinePaymentOptions, isOfflinePayment, DELIVERY_LABELS } from "../components/OfflinePaymentOptions";

const Field = ({ label, name, value, onChange, type = "text", required = true, placeholder }) => (
    <div>
        <label className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">
            {label}{required && " *"}
        </label>
        <input
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            placeholder={placeholder}
            data-testid={`checkout-${name}-input`}
            className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all"
        />
    </div>
);

const Checkout = () => {
    const { items, subtotal, clearCart } = useCart();
    const navigate = useNavigate();
    const [creatingPending, setCreatingPending] = useState(false);
    const [showCardPayment, setShowCardPayment] = useState(false);
    const [pendingOrder, setPendingOrder] = useState(null); // { id, order_number, total } — real Order row
    const [completedOrder, setCompletedOrder] = useState(null);
    const [offline, setOffline] = useState({ method: "cash", delivery: "drop_off" });
    const [couponInput, setCouponInput] = useState("");
    const [applied, setApplied] = useState(null); // { code, discount, campaign }
    const [applying, setApplying] = useState(false);
    const [roundupEnabled, setRoundupEnabled] = useState(true);
    const [fulfillmentType, setFulfillmentType] = useState("ship"); // "ship" | "in_person_pickup"
    // Live shipping-rate quote (ShipStation-backed with flat fallback).
    // Populated by an effect that fires as soon as we have a valid zip + items.
    const [rateOptions, setRateOptions] = useState([]); // [{carrier, service, service_code, amount, est_delivery_days}]
    const [selectedRateCode, setSelectedRateCode] = useState("");
    const [rateSource, setRateSource] = useState(""); // "shipstation" | "flat_rule" | ""
    const [rateLoading, setRateLoading] = useState(false);
    const [rateError, setRateError] = useState("");

    const discount = applied?.discount || 0;
    const discounted = Math.max(subtotal - discount, 0);
    const isPickup = fulfillmentType === "in_person_pickup";
    const selectedRate = rateOptions.find((r) => r.service_code === selectedRateCode);
    // Shipping = pickup (0) OR the customer-selected rate OR $0 until they
    // pick one. Previously we fell back to the $7.50 flat rate while rates
    // loaded, which flashed a wrong number in the summary. Now the summary
    // stays at "Pick a method →" until a real rate is selected.
    const shipping = isPickup ? 0 : (selectedRate?.amount ?? 0);
    const shippingReady = isPickup || selectedRate != null;
    const preRoundupTotal = discounted + shipping;
    // Round up to the next whole dollar — that's the suggested round-up amount.
    const roundupAmount = roundupEnabled && preRoundupTotal > 0
        ? Math.max(0, Math.min(99, Number((Math.ceil(preRoundupTotal) - preRoundupTotal).toFixed(2))))
        : 0;
    const total = preRoundupTotal + roundupAmount;

    const [form, setForm] = useState({
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        shipping_address: "",
        shipping_city: "",
        shipping_state: "",
        shipping_zip: "",
        notes: "",
    });

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    // Fetch live carrier rates whenever we have a valid zip + items + ship mode.
    // Debounced by 350ms so the user typing "08865" doesn't hammer the API.
    // Falls back to a flat "Standard shipping" option if ShipStation isn't
    // configured (backend returns source='flat_rule' so we surface that).
    useEffect(() => {
        if (isPickup || items.length === 0 || !form.shipping_zip || form.shipping_zip.length < 5) {
            setRateOptions([]); setSelectedRateCode(""); setRateSource(""); setRateError("");
            return;
        }
        setRateLoading(true);
        setRateError("");
        const handle = setTimeout(async () => {
            try {
                const { data } = await api.post("/shipstation/quote", {
                    to_postal_code: form.shipping_zip,
                    to_state: form.shipping_state,
                    to_country: "US",
                    items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
                });
                setRateOptions(data.rates || []);
                // DON'T auto-select — customer must consciously pick a rate so
                // the summary shipping cost doesn't jump around beneath them.
                setSelectedRateCode("");
                setRateSource(data.source || "");
            } catch (err) {
                setRateError(err.response?.data?.detail || "Couldn't fetch shipping rates");
                setRateOptions([]);
                setSelectedRateCode("");
            } finally {
                setRateLoading(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [isPickup, items, form.shipping_zip, form.shipping_state]);

    const applyCoupon = async () => {
        if (!couponInput.trim()) return;
        setApplying(true);
        try {
            const { data } = await api.post("/coupons/validate", {
                code: couponInput.trim().toUpperCase(),
                subtotal,
            });
            setApplied(data);
            toast.success(`Code ${data.code} applied · -$${data.discount.toFixed(2)}`);
        } catch (err) {
            toast.error(formatApiError(err, "Invalid code"));
        } finally {
            setApplying(false);
        }
    };

    const removeCoupon = () => {
        setApplied(null);
        setCouponInput("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        // No-op: the form no longer creates the paid order directly. Payment
        // now runs through PayPal or Stripe below — clicking the buttons
        // creates a pending order (via createPendingOrder) then hands off to
        // the payment gateway. The old "Pay $X" bypass button has been removed.
    };

    // Create (or return existing) pending order row on the backend.
    // The returned order.id is used as `cartId` for PayPal/Stripe so the
    // capture handler / webhook can finalize the right row.
    const requiredFieldsFilled = () => {
        if (!form.customer_name.trim() || !form.customer_email.trim()) return false;
        if (!(form.shipping_address.trim() && form.shipping_city.trim() && form.shipping_state.trim() && form.shipping_zip.trim())) return false;
        if (fulfillmentType === "ship") {
            // Must also have picked a shipping method — no proceeding until
            // the customer knows their real total including postage.
            if (!selectedRate) return false;
        }
        return true;
    };

    const createPendingOrder = async (paymentMethod) => {
        // Reuse the same pending order for repeated payment attempts (donor might swap PayPal ↔ Stripe).
        if (!requiredFieldsFilled()) {
            toast.error("Please enter your contact details and customer address first");
            throw new Error("missing_fields");
        }
        const checkoutFingerprint = JSON.stringify({ form, fulfillmentType, selectedRateCode, items, coupon: applied?.code, roundupAmount, offline });
        if (pendingOrder && pendingOrder.payment_method === paymentMethod && pendingOrder.checkoutFingerprint === checkoutFingerprint) return pendingOrder;
        setCreatingPending(true);
        try {
            // If any item in the cart matches a QR-attributed product, tag
            // the order for analytics. Only fires when the scanned product
            // is actually being purchased (prevents cross-attribution).
            const qrAttrib = items
                .map((it) => getQrAttribution(it.product_id))
                .find((r) => r) || null;
            const { data } = await api.post("/orders", {
                ...form,
                items,
                coupon_code: applied?.code,
                roundup_amount: roundupAmount,
                fulfillment_type: fulfillmentType,
                payment_method: paymentMethod,
                payment_delivery: isOfflinePayment(paymentMethod) ? offline.delivery : null,
                shipping_rate_amount: isPickup ? 0 : (selectedRate?.amount ?? null),
                shipping_service_code: isPickup ? null : (selectedRate?.service_code ?? null),
                ...(qrAttrib ? { qr_source: qrAttrib.target_id, qr_kind: qrAttrib.kind } : {}),
            });
            setPendingOrder({ ...data, payment_method: paymentMethod, checkoutFingerprint });
            return data;
        } catch (err) {
            toast.error(formatApiError(err, "Check your contact details and customer address, then try again."));
            throw err;
        } finally {
            setCreatingPending(false);
        }
    };

    const onPaymentSuccess = (orderArg) => {
        // For gateway payments (Stripe/PayPal), pendingOrder is already
        // set by createPendingOrder(). For COD we pass the fresh order
        // directly since it never went through a gateway callback.
        const finalOrder = orderArg && orderArg.id ? orderArg : pendingOrder;
        if (finalOrder) setCompletedOrder(finalOrder);
        clearCart();
        toast.success(
            isOfflinePayment(finalOrder?.payment_method)
                ? "Order reserved — awaiting cash/check confirmation"
                : "Payment received — thank you!"
        );
    };

    if (completedOrder) {
        const isCod = isOfflinePayment(completedOrder.payment_method);
        const isPickup = completedOrder.fulfillment_type === "in_person_pickup";
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="checkout-success">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-8 h-8 text-emerald-700" strokeWidth={2} />
                </div>
                <h1 className="font-heading text-4xl text-[#1C252E] font-medium mb-3">
                    {isCod ? "Order confirmed!" : "Thank you!"}
                </h1>
                <p className="text-[#4A5568] mb-2">
                    {isCod
                        ? "Your order is saved and your items are reserved. Payment will be confirmed when your cash or check arrives."
                        : "Your order has been placed."}
                </p>
                <p className="text-sm text-[#4A5568] mb-8">
                    Confirmation #<span className="font-bold text-[#1C252E]" data-testid="order-number">{completedOrder.order_number}</span>
                </p>
                <div className="bg-white border border-black/5 rounded-2xl p-8 text-left space-y-4">
                    <p className="text-sm text-[#4A5568]">
                        A confirmation email is on its way to <strong className="text-[#1C252E]">{completedOrder.customer_email}</strong>.
                    </p>
                    {isCod && (
                        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="checkout-success-cod-details">
                            <p data-testid="checkout-offline-payment-status" className="text-[10px] uppercase tracking-[0.14em] font-bold text-amber-800 mb-1">Awaiting {completedOrder.payment_method === "cod" ? "cash/check" : completedOrder.payment_method} · {DELIVERY_LABELS[completedOrder.payment_delivery] || "Payment pending"}</p>
                            <p className="font-heading text-2xl text-amber-900 font-semibold">${completedOrder.total.toFixed(2)}</p>
                            <p className="text-xs text-amber-800 mt-2">
                                Cash or check payable to <strong>Reddog Community Foundation Inc.</strong>. Mail checks to <span className="font-mono">PO Box 231, Frenchtown, MT 59834</span> or call <a href="tel:+14068020279" className="underline font-semibold">(406) 802-0279</a> to arrange pickup. We&apos;ll hold your order for 30 days.
                            </p>
                        </div>
                    )}
                    {!isCod && (
                        <div className="flex justify-between text-sm border-t border-black/10 pt-4">
                            <span className="text-[#4A5568]">Total paid</span>
                            <span className="font-heading text-lg font-medium text-[#1C252E]">${completedOrder.total.toFixed(2)}</span>
                        </div>
                    )}
                    {isPickup && (
                        <p className="text-xs text-[#4A5568] border-t border-black/10 pt-4">
                            Pickup: reply to your confirmation email to arrange a time, or watch for our next follow-up.
                        </p>
                    )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 justify-center mt-10">
                    <Link
                        to="/account/orders"
                        data-testid="checkout-view-order-link"
                        className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-8 py-3.5 font-medium transition-all"
                    >
                        View order
                    </Link>
                    <Link
                        to="/shop"
                        data-testid="checkout-continue-link"
                        className="inline-flex items-center justify-center gap-2 border border-black/15 text-[#1C252E] hover:bg-[#F4F1EA] rounded-full px-8 py-3.5 font-medium transition-all"
                    >
                        Keep shopping
                    </Link>
                </div>
            </div>
        );
    }

    if (items.length === 0) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-20 text-center">
                <p className="text-[#4A5568] mb-6">Your cart is empty.</p>
                <Link to="/shop" className="text-[#E53935] underline">Browse products</Link>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-16" data-testid="checkout-page">
            <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium tracking-tight mb-12">
                Checkout
            </h1>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-2 space-y-8">
                    <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8 space-y-5">
                        <h2 className="font-heading text-xl text-[#1C252E] font-medium mb-2">Contact</h2>
                        <Field label="Full name" name="customer_name" value={form.customer_name} onChange={handleChange} placeholder="Jane Doe" />
                        <div className="grid sm:grid-cols-2 gap-5">
                            <Field label="Email" name="customer_email" value={form.customer_email} onChange={handleChange} type="email" placeholder="you@email.com" />
                            <Field label="Phone" name="customer_phone" value={form.customer_phone} onChange={handleChange} required={false} placeholder="(555) 555-5555" />
                        </div>
                    </div>

                    <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="checkout-fulfillment-section">
                        <h2 className="font-heading text-xl text-[#1C252E] font-medium mb-4">Delivery</h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <label
                                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all ${fulfillmentType === "ship" ? "border-[#E53935] bg-[#F2D8D5]/40" : "border-black/10 hover:border-[#E53935]/40"}`}
                                data-testid="checkout-fulfillment-ship-label"
                            >
                                <input
                                    type="radio"
                                    name="fulfillment_type"
                                    checked={fulfillmentType === "ship"}
                                    onChange={() => setFulfillmentType("ship")}
                                    data-testid="checkout-fulfillment-ship"
                                    className="mt-1 accent-[#E53935]"
                                />
                                <div>
                                    <p className="text-sm font-medium text-[#1C252E]">Ship to me</p>
                                    <p className="text-[11px] text-[#4A5568] mt-0.5">Live USPS &amp; UPS rates via ShipStation — pick a method once your address is entered.</p>
                                </div>
                            </label>
                            <label
                                className={`flex items-start gap-3 rounded-xl border p-4 cursor-pointer transition-all ${isPickup ? "border-[#E53935] bg-[#F2D8D5]/40" : "border-black/10 hover:border-[#E53935]/40"}`}
                                data-testid="checkout-fulfillment-pickup-label"
                            >
                                <input
                                    type="radio"
                                    name="fulfillment_type"
                                    checked={isPickup}
                                    onChange={() => setFulfillmentType("in_person_pickup")}
                                    data-testid="checkout-fulfillment-pickup"
                                    className="mt-1 accent-[#E53935]"
                                />
                                <div>
                                    <p className="text-sm font-medium text-[#1C252E]">In-person pickup</p>
                                    <p className="text-[11px] text-[#4A5568] mt-0.5">Free — grab your order at the next event or from the board. We&apos;ll email a pickup window.</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="checkout-address-section">
                        <h2 data-testid="checkout-address-heading" className="font-heading text-xl text-[#1C252E] font-medium mb-2">{isPickup ? "Customer address" : "Shipping address"}</h2>
                        <Field label="Street address" name="shipping_address" value={form.shipping_address} onChange={handleChange} placeholder="123 Main Street" />
                        <div className="grid sm:grid-cols-3 gap-5">
                            <Field label="City" name="shipping_city" value={form.shipping_city} onChange={handleChange} placeholder="Springfield" />
                            <Field label="State" name="shipping_state" value={form.shipping_state} onChange={handleChange} placeholder="IL" />
                            <Field label="ZIP" name="shipping_zip" value={form.shipping_zip} onChange={handleChange} placeholder="62701" />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">Notes</label>
                            <textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleChange}
                                rows={3}
                                placeholder="Anything we should know?"
                                data-testid="checkout-notes-input"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all resize-none"
                            />
                        </div>
                    </div>
                    {/* Live shipping rate picker — driven by ShipStation with a
                        flat-rule fallback when unconfigured or during API outages. */}
                    {!isPickup && form.shipping_zip && form.shipping_zip.length >= 5 && (
                        <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="checkout-rates-section">
                            <div className="flex items-baseline justify-between mb-3">
                                <h2 className="font-heading text-xl text-[#1C252E] font-medium">Shipping method</h2>
                                {rateSource === "shipstation" && (
                                    <span className="text-[10px] uppercase tracking-wider text-[#2E7D32] font-bold" data-testid="checkout-rates-source">Live rates</span>
                                )}
                                {rateSource === "flat_rule" && (
                                    <span className="text-[10px] uppercase tracking-wider text-[#4A5568] font-bold" data-testid="checkout-rates-source">Standard</span>
                                )}
                            </div>
                            {rateLoading && (
                                <p className="text-sm text-[#4A5568]" data-testid="checkout-rates-loading">Fetching live rates for {form.shipping_zip}…</p>
                            )}
                            {rateError && !rateLoading && (
                                <p className="text-sm text-red-600" data-testid="checkout-rates-error">{rateError}</p>
                            )}
                            {!rateLoading && rateOptions.length > 0 && (
                                <div className="space-y-2" data-testid="checkout-rates-list">
                                    {rateOptions.map((r) => (
                                        <label
                                            key={r.service_code}
                                            className={`flex items-center justify-between gap-3 rounded-xl border p-3 cursor-pointer transition-all ${selectedRateCode === r.service_code ? "border-[#E53935] bg-[#F2D8D5]/40" : "border-black/10 hover:border-[#E53935]/40"}`}
                                            data-testid={`checkout-rate-option-${r.service_code}`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="shipping_rate"
                                                    checked={selectedRateCode === r.service_code}
                                                    onChange={() => setSelectedRateCode(r.service_code)}
                                                    className="accent-[#E53935]"
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-[#1C252E]">
                                                        {r.service}
                                                        {r.carrier !== "flat" && <span className="text-[10px] uppercase tracking-wider text-[#4A5568] ml-2">{r.carrier}</span>}
                                                    </p>
                                                    {r.est_delivery_days && (
                                                        <p className="text-[11px] text-[#4A5568]">Est. {r.est_delivery_days} business day{r.est_delivery_days > 1 ? "s" : ""}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="font-heading text-lg font-medium text-[#1C252E]" data-testid={`checkout-rate-amount-${r.service_code}`}>
                                                {r.amount === 0 ? "Free" : `$${r.amount.toFixed(2)}`}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {isPickup && (
                        <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="checkout-pickup-notes-section">
                            <label className="text-xs uppercase tracking-[0.12em] font-bold text-[#4A5568] block mb-2">Pickup notes</label>
                            <textarea
                                name="notes"
                                value={form.notes}
                                onChange={handleChange}
                                rows={3}
                                placeholder="When can you pick up? Any special requests?"
                                data-testid="checkout-notes-input"
                                className="w-full rounded-xl border border-black/10 px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all resize-none"
                            />
                        </div>
                    )}

                    <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8" data-testid="checkout-payment-section">
                        <h2 className="font-heading text-xl text-[#1C252E] font-medium mb-2">Payment</h2>
                        <p className="text-sm text-[#4A5568] mb-5">Secure PayPal checkout — no PayPal account required, any credit or debit card works.</p>
                        {!requiredFieldsFilled() ? (
                            <div className="rounded-xl bg-[#F4F1EA] border border-black/5 px-4 py-4 text-sm text-[#4A5568]" data-testid="checkout-payment-locked">
                                {fulfillmentType === "ship" && !selectedRate && form.shipping_zip
                                    ? "Choose a shipping method above to unlock payment."
                                    : "Enter your contact details and customer address above to continue."}
                            </div>
                        ) : (
                            <>
                                <PayPalCheckoutButtons
                                    purpose="shop"
                                    amount={total}
                                    getReferenceId={async () => {
                                        const o = await createPendingOrder("paypal");
                                        return o.id;
                                    }}
                                    donorName={form.customer_name}
                                    donorEmail={form.customer_email}
                                    description={`Reddog shop order (${items.length} item${items.length === 1 ? "" : "s"})`}
                                    onSuccess={onPaymentSuccess}
                                    disabled={creatingPending}
                                />
                                {total > 0 && (
                                    <div className="mt-4 space-y-3" data-testid="checkout-stripe-section">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-px bg-black/10" />
                                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Or pay with card</p>
                                            <div className="flex-1 h-px bg-black/10" />
                                        </div>
                                        {!showCardPayment ? <button data-testid="checkout-show-card-payment" type="button" disabled={creatingPending} onClick={() => setShowCardPayment(true)} className="w-full rounded-full border border-black/15 px-5 py-3 text-sm font-medium hover:bg-[#F4F1EA] transition-colors disabled:opacity-50">Pay with card</button> : <StripePaymentForm
                                            purpose="shop"
                                            amount={total}
                                            getCartId={async () => {
                                                const o = await createPendingOrder("stripe");
                                                return o.id;
                                            }}
                                            donorName={form.customer_name}
                                            donorEmail={form.customer_email}
                                            onSuccess={onPaymentSuccess}
                                        />}
                                    </div>
                                )}
                                {/* Cash on pickup — only visible for in-person pickup orders. */}
                                {total > 0 && (
                                    <div className="mt-4 space-y-3" data-testid="checkout-cod-section">
                                        <div className="flex items-center gap-3">
                                            <div className="flex-1 h-px bg-black/10" />
                                            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">Or pay by cash/check</p>
                                            <div className="flex-1 h-px bg-black/10" />
                                        </div>
                                        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                                            <p className="text-sm text-emerald-900 mb-3">
                                                Reserve your items now. Drop off cash or a check payable to <strong>Reddog Community Foundation Inc.</strong>.
                                                Prefer to mail a check? Send to <span className="font-mono">PO Box 231, Frenchtown, MT 59834</span> or call <a href="tel:+14068020279" className="underline font-semibold">(406) 802-0279</a>.
                                                We&apos;ll hold your order for 30 days.
                                            </p>
                                            <div className="mb-4"><OfflinePaymentOptions prefix="checkout-offline" method={offline.method} delivery={offline.delivery} onChange={(method, delivery) => setOffline({ method, delivery })} disabled={creatingPending} /></div>
                                            <button
                                                type="button"
                                                data-testid="checkout-cod-btn"
                                                disabled={creatingPending}
                                                onClick={async () => {
                                                    try {
                                                        const o = await createPendingOrder(offline.method);
                                                        onPaymentSuccess(o);
                                                    } catch (e) {
                                                        toast.error(formatApiError(e, "Couldn't place cash order"));
                                                    }
                                                }}
                                                className="inline-flex items-center justify-center gap-2 w-full bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 py-3 font-medium transition-all disabled:opacity-60"
                                            >
                                                {creatingPending ? "Reserving…" : `Reserve $${total.toFixed(2)} · pay by ${offline.method}`}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        <p className="text-xs text-[#4A5568] mt-3 flex items-center gap-2">
                            <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                            SSL-encrypted. Reddog Foundation does not store your card details — PayPal handles them.
                        </p>
                    </div>
                </div>

                <div className="bg-white border border-black/5 rounded-2xl p-6 sm:p-8 h-fit lg:sticky lg:top-24">
                    <h2 className="font-heading text-xl text-[#1C252E] font-medium mb-6">Order summary</h2>
                    <div className="space-y-4 mb-6">
                        {items.map((i) => (
                            <div key={i.product_id} className="flex gap-3 items-center" data-testid={`summary-item-${i.product_id}`}>
                                <img src={i.image_url || undefined} alt={i.name} className="w-14 h-14 object-cover rounded-lg bg-[#F4F1EA]" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-[#1C252E] truncate">{i.name}</p>
                                    <p className="text-xs text-[#4A5568]">Qty: {i.quantity}</p>
                                </div>
                                <p className="text-sm font-medium text-[#1C252E]">${(i.price * i.quantity).toFixed(2)}</p>
                            </div>
                        ))}
                    </div>
                    <div className="border-t border-black/10 pt-4 space-y-3 text-sm">
                        <div className="flex justify-between text-[#4A5568]">
                            <span>Subtotal</span>
                            <span className="text-[#1C252E]">${subtotal.toFixed(2)}</span>
                        </div>
                        {applied && (
                            <div className="flex justify-between text-[#E53935] font-medium" data-testid="checkout-discount-row">
                                <span className="flex items-center gap-1">
                                    <Tag className="w-3.5 h-3.5" strokeWidth={1.5} />
                                    {applied.code}
                                    <button type="button" onClick={removeCoupon} className="ml-1 hover:text-[#1C252E]" data-testid="checkout-remove-coupon">
                                        <X className="w-3 h-3" strokeWidth={2} />
                                    </button>
                                </span>
                                <span>-${discount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-[#4A5568]">
                            <span>Shipping</span>
                            <span className="text-[#1C252E]" data-testid="checkout-shipping-cell">
                                {!shippingReady
                                    ? <span className="text-[#4A5568]">Pick a method →</span>
                                    : (shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`)}
                            </span>
                        </div>
                    </div>

                    {/* Round-up at checkout — funds the General Support Fund */}
                    {preRoundupTotal > 0 && (
                        <label
                            className={`mt-4 flex items-start gap-3 rounded-2xl border p-4 cursor-pointer select-none transition-all ${
                                roundupEnabled
                                    ? "bg-[#F2D8D5] border-[#E53935]/40"
                                    : "bg-[#FDFBF7] border-black/10 hover:border-[#E53935]/30"
                            }`}
                            data-testid="checkout-roundup-label"
                        >
                            <input
                                type="checkbox"
                                checked={roundupEnabled}
                                onChange={(e) => setRoundupEnabled(e.target.checked)}
                                data-testid="checkout-roundup-checkbox"
                                className="mt-1 w-4 h-4 accent-[#E53935]"
                            />
                            <div className="flex-1 text-xs leading-relaxed">
                                <p className="font-bold text-[#E53935] uppercase tracking-wider mb-1 flex items-center gap-1.5">
                                    <Heart className="w-3 h-3" fill="#E53935" strokeWidth={0} />
                                    Round up for the General Support Fund
                                </p>
                                <p className="text-[#4A5568]">
                                    {roundupAmount > 0 ? (
                                        <>
                                            Round <strong>${preRoundupTotal.toFixed(2)}</strong> up to <strong>${(preRoundupTotal + (Math.ceil(preRoundupTotal) - preRoundupTotal)).toFixed(2)}</strong> —
                                            an extra{" "}
                                            <strong data-testid="checkout-roundup-amount">${roundupAmount.toFixed(2)}</strong>{" "}
                                            goes directly to support classroom needs &amp; community projects.
                                        </>
                                    ) : (
                                        <>Your order already rounds out evenly — no round-up applied.</>
                                    )}
                                </p>
                            </div>
                        </label>
                    )}

                    {roundupAmount > 0 && (
                        <div className="flex justify-between text-[#E53935] text-sm mt-3 font-medium" data-testid="checkout-roundup-row">
                            <span className="flex items-center gap-1.5">
                                <Heart className="w-3.5 h-3.5" fill="#E53935" strokeWidth={0} />
                                Round-up donation
                            </span>
                            <span>+${roundupAmount.toFixed(2)}</span>
                        </div>
                    )}

                    {/* Coupon input */}
                    {!applied && (
                        <div className="mt-4 flex gap-2" data-testid="checkout-coupon-section">
                            <input
                                type="text"
                                value={couponInput}
                                onChange={(e) => setCouponInput(e.target.value)}
                                placeholder="Promo code"
                                data-testid="checkout-coupon-input"
                                className="flex-1 rounded-xl border border-black/10 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] transition-all uppercase"
                            />
                            <button
                                type="button"
                                onClick={applyCoupon}
                                disabled={applying || !couponInput.trim()}
                                data-testid="checkout-apply-coupon-btn"
                                className="px-5 py-2.5 rounded-xl text-sm font-medium border border-[#1C252E] text-[#1C252E] hover:bg-[#1C252E] hover:text-white transition-all disabled:opacity-50"
                            >
                                {applying ? "…" : "Apply"}
                            </button>
                        </div>
                    )}

                    <div className="border-t border-black/10 mt-4 pt-4 flex justify-between mb-2">
                        <span className="font-heading text-lg font-medium text-[#1C252E]">Total</span>
                        <span className="font-heading text-2xl font-medium text-[#1C252E]" data-testid="checkout-total">${total.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-[#4A5568]" data-testid="checkout-payment-hint">
                        Complete payment above via PayPal or card to place your order.
                    </p>
                </div>
            </form>
        </div>
    );
};

export default Checkout;

